import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Typography,
  Descriptions,
  Spin,
  Alert,
} from "antd";
import { reviewFetchDetails } from "../../../services/review-fetch-details";
import { getUserEmail } from "../../../services/utils";
import "./reviewUpload.css";
import { environmentNames } from "../data-upload/constants";
import ReviewComplete from "./review-complete";
import { useUser } from "../../../user-context";
import { useOpenNotification } from "../../../components/Notification/notification";
import {
  descriptionLabel,
  eTMFWarningTextReviewPage,
} from "../../../components/Shared/utils";
import { SaveDraft } from "../../../services/save-draft";
import { REVIEW_ENV_NAME } from "./constants";
import GenericDialog from "../../../components/GenericDialog/generic-dialog";
import {
  InfoCircleOutlined,
  InfoCircleFilled,
  PaperClipOutlined,
} from "@ant-design/icons";
import { dataUploadFetchDetails } from "../../../services/data-upload-fetch-details";

interface ReviewUploadProps {
  reviewData?: any;
  recordId?: string;
}
interface DeliverableItem {
  filename: boolean;
  comment: string;
}

interface FormValues {
  // protocol_number: any;
  record_id?: string;
  reviewer_description?: string;
  status?: string;
  rejection_reason?: string;
  environment?: string;
  updated_by?: string;
}

const ReviewUpload: React.FC<ReviewUploadProps> = ({
  reviewData,
  recordId,
}) => {
  const { Text } = Typography;
  const [reviewDetails, setReviewDetails] = useState<any>(null);
  const [reviewComplete, setReviewComplete] = useState<boolean>(false);
  const [reviewRejectDetails, setReviewRejectDetails] = React.useState({
    title: "",
    id: "",
    subtitle: "",
  });
  const [open, setOpen] = useState(false);
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const { openNotification, contextHolder } = useOpenNotification();
  const [loading, setLoading] = useState<boolean>(false);
  const { userRole } = useUser();
  const reviewFormRef = React.useRef<any>(null);
  const userEmail = getUserEmail();
  const uploadedFiles = reviewDetails?.supplier_upload_file_names?.[0]?.fileNames || [];
  const previewFiles = reviewDetails?.supplier_upload_file_names?.[0]?.previewFiles || [];

  const showPopconfirm = () => {
    setOpen(true);
  };

  const formData = (getFormValue: any, status: any): FormValues | undefined => {
    const formValues: FormValues = {
      record_id: recordId ?? "",
      status: status ?? "",
      reviewer_description: getFormValue?.description ?? "",
      updated_by: userEmail ?? "",
      environment: reviewData?.transfer_location,
    };
    if (status === "Reject Transfer") {
      // Check if the description field is empty
      formValues.rejection_reason = rejectionReason ?? "";
      formValues.environment = reviewData?.transfer_location;
      return formValues;
    }
    return formValues;
  };

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  const waitForFinalizeCompletion = async () => {
    if(recordId) {
      for (let i = 0; i < 120; i++) {
        const fetchedData = await dataUploadFetchDetails(recordId);

        if (
          fetchedData?.processing_status === "COMPLETED" ||
          (fetchedData?.status === "Finalize Transfer" && fetchedData?.processing_status !== "PROCESSING")
        ) {
          return true;
        }

        if (fetchedData?.processing_status === "FAILED") {
          return false;
        }
        await sleep(5000);
      }
    }
    return false;
  };

  const waitForRejectCompletion = async () => {
    if(recordId) {
      for (let i = 0; i < 120; i++) {
        const fetchedData = await dataUploadFetchDetails(recordId);

        if (fetchedData?.processing_status !== "PROCESSING") {
          if (fetchedData?.processing_status === "FAILED") {
            return false;
          }
          return true;
        }
        await sleep(5000);
      }
    }
    return false;
  };

  const handleFinalizeTransfer = async () => {
    const saveDraftFormValues = reviewFormRef.current?.getFieldsValue();
    const status = "Finalize Transfer";
    const getFormValues = formData(saveDraftFormValues, status);
    setLoading(true);
    try {
      await SaveDraft(getFormValues);
    } catch (error: any) {
      if (error?.message === "Endpoint request timed out") {
        openNotification("info", "Finalizing the transfer, please wait.");
      } else {
        openNotification("error", "Error occurred while saving draft.");
        setLoading(false);
      }
    }

    try {
      const isSuccess = await waitForFinalizeCompletion();

      if (isSuccess) {
        setReviewComplete(true);
        setReviewRejectDetails({
          title: REVIEW_ENV_NAME.approved_title,
          id: "approved",
          subtitle: REVIEW_ENV_NAME.approved_subtitle,
        });
      } else {
        openNotification("error", "Finalization processing failed. Please try again..");
      }
    } catch (pollError) {
      openNotification("error", "An error occurred while checking status.");
    }
    setLoading(false);
  };

  const handleRejectTransfer = async () => {
    const saveDraftRejectFormValues = reviewFormRef.current?.getFieldsValue();
    const status = "Reject Transfer";
    const getRejectFormValues = formData(saveDraftRejectFormValues, status);
    setConfirmLoading(true);
    try {
      await SaveDraft(getRejectFormValues);
    } catch (error: any) {
      if (error?.message === "Endpoint request timed out") {
        openNotification("info", "Rejecting the transfer, please wait.");
      } else {
        openNotification("error", error?.message ?? "Failed to reject the transfer");
        setConfirmLoading(false);
        return;
      }
    }

    try {
      const isSuccess = await waitForRejectCompletion();
      if (isSuccess) {
        setReviewComplete(true);
        setReviewRejectDetails({
          title: REVIEW_ENV_NAME.reject_title,
          id: "rejected",
          subtitle: REVIEW_ENV_NAME.reject_subtitle,
        });
      } else {
        openNotification("error", "Rejection processing failed. Please try again.");
      }
    } catch (pollError) {
      openNotification("error", "An error occurred while checking rejection status.");
    }
    setConfirmLoading(false);
  };

  const handleCancel = () => {
    setOpen(false);
    setRejectionReason(""); // reset input on cancel
  };

  const handleSubmitETMFWarning = async () => {
    setOpenWarningDialog(false);
    handleFinalizeTransfer();
  };

  const handleETMFWarningDialog = async () => {
    try {
      await reviewFormRef.current?.validateFields();
      if (reviewDetails?.etmf_upload_flag === false) {
        setOpenWarningDialog(true);
      } else {
        handleFinalizeTransfer();
      }
    } catch (error) {
      return;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [reviewComplete]);

  useEffect(() => {
    if (recordId) {
      reviewFetchDetails(recordId).then((fetchedData) => {
        if (fetchedData) {
          setReviewDetails(fetchedData);
        }
      });
    }
  }, [recordId]);
  // Prepare the details fields for Descriptions
  const reviewFieldsValue = [
    {
      key: "1",
      label: "Transfer Type",
      children: reviewDetails?.transfer_id ?? "N/A",
    },
    {
      key: "2",
      label: "Transfer Location",
      children: environmentNames[reviewDetails?.transfer_location] ?? "N/A",
    },
    {
      key: "3",
      label: "Uploaded File",
      // span:2,
      children:
        uploadedFiles &&
        (uploadedFiles).map(
          (file: any, index: number) => (
            <div key={index}>
              <PaperClipOutlined />{" "}
              <span style={{ color: "#008576" }}>{file}</span>
            </div>
          ),
        ),
      // children: reviewDetails?.supplierCompound ?? 'N/A',
    },
    {
      label: "File Contents",
      children:
        previewFiles?.length > 0 ? (
          (previewFiles).map((file: any) => (
            <div key={file}>
              <span style={{ color: "#008576" }}>{file}</span>
            </div>
          ))
        ) : (
          <div>preview files are not available</div>
        ),
    },
    {
      key: "4",
      label: "Description",
      // span:1,
      children: reviewDetails?.supplier_description ?? "N/A",
    },
  ];

  return (
    <>
      {reviewComplete === true ? (
        <div>
          <Card variant="borderless">
            <div id="initiate-transfer" className="initiate">
              <ReviewComplete transferDetails={reviewRejectDetails} />
            </div>
          </Card>
        </div>
      ) : (
        <>
          {reviewDetails ? (
            <div>
              <div className="card-section">
                <Card variant="borderless">
                  <div id="review-upload" className="initiate">
                    <div className="review-desc-section">
                      <Descriptions
                        column={1}
                        size="small"
                        title="Review"
                        bordered
                        items={reviewFieldsValue}
                      />
                    </div>
                    <div>
                      {reviewDetails?.stdm_deliverables &&
                        Object?.keys(reviewDetails?.stdm_deliverables).length >
                          0 && (
                          <Descriptions
                            title="Transfer Deliverables"
                            bordered
                            column={1}
                            size="small"
                            style={{ marginTop: "20px" }}
                          >
                            {(
                              Object.entries(
                                reviewDetails?.stdm_deliverables,
                              ) as [string, DeliverableItem][]
                            )
                              .filter(([_, value]) => value?.filename)
                              .map(([key, value], index) => (
                                <Descriptions.Item
                                  key={index}
                                  label={value?.filename}
                                >
                                  {value.comment}
                                </Descriptions.Item>
                              ))}
                          </Descriptions>
                        )}
                    </div>
                  </div>
                </Card>
              </div>
              <div className="card-section">
                <Card variant="borderless">
                  <div id="review-description" className="initiate">
                      <Form
                        ref={reviewFormRef}
                        layout="vertical"
                        requiredMark={false}
                      >
                        <div style={ { fontWeight: 400, fontSize: "20px", lineHeight: "28px", letterSpacing: "0%" } }>
                          {
                            <>
                              <span style={{ color: "red" }}>* </span>Description &nbsp;
                            </>
                          }
                        </div>
                        <Form.Item
                          label={descriptionLabel(true)}
                          name="description"
                          rules={[
                            {
                              required: true,
                              message: "This is a required field",
                            },
                          ]}
                        >
                          <Input.TextArea
                            disabled={userRole?.toLowerCase() === "supplier"}
                          />
                        </Form.Item>
                      </Form>
                  </div>
                </Card>
              </div>
              <div className="card-section">
                <Card variant="borderless" title="Review">
                  {reviewDetails?.etmf_upload_flag === false && (
                    <Alert
                      message={eTMFWarningTextReviewPage()}
                      type="info"
                      showIcon
                      icon={<InfoCircleFilled style={{ color: "#008576" }} />}
                      style={{
                        borderRadius: 0,
                        borderColor: "#00855a24",
                        background: "#00855a24",
                        marginBottom: "10px",
                      }}
                    />
                  )}
                  <div className="review-section">
                    <Text>Is the transfer ready to be finalized?</Text>
                  </div>

                  <div id="transfer-complete">
                    <Button
                      className="finalize-transfer"
                      loading={loading}
                      type="primary"
                      onClick={handleETMFWarningDialog}
                      disabled={userRole?.toLowerCase() === "supplier" || confirmLoading}
                      htmlType="submit"
                    >
                      {" "}
                      Finalize Transfer{" "}
                    </Button>
                    <Button
                      className="reject-transfer"
                      onClick={showPopconfirm}
                      disabled={userRole?.toLowerCase() === "supplier" || loading}
                    >
                      {" "}
                      Reject Transfer{" "}
                    </Button>
                  </div>
                </Card>
              </div>
              {reviewDetails?.etmf_upload_flag === false && (
                <GenericDialog
                  open={openWarningDialog}
                  submitButtonText="Confirm Finalize Transfer"
                  cancelButtonText="Cancel"
                  handleSubmit={handleSubmitETMFWarning}
                  handleCancel={() => {
                    setOpenWarningDialog(false);
                  }}
                  width={450}
                  title="Continue"
                  titleIcon={
                    <InfoCircleOutlined
                      style={{ color: "orange", marginRight: "15px" }}
                    />
                  }
                  description="The MSD Compound/Protocol for this transfer setup does not have a folder existing in eTMF. If you continue with finalizing this data transfer, you will be responsible for manually uploading the associated transfer package level report into eTMF."
                />
              )}
              {
                  open && <GenericDialog
                    open={open}
                    submitButtonText="Reject"
                    okButtonLoading={confirmLoading}
                    isDisabled={!rejectionReason.trim()}
                    cancelButtonText="Cancel"
                    handleSubmit={handleRejectTransfer}
                    handleCancel={handleCancel}
                    width={450}
                    title="Reject Transfer"
                    titleIcon={
                      <InfoCircleOutlined
                        style={{ color: "orange", marginRight: "15px" }}
                      />
                    }
                    description={
                      <>
                        <div>
                          By rejecting the transfer, you will be sending it
                          back to the supplier to edit. Please explain your
                          rejection below.
                        </div>
                        <Input.TextArea
                          rows={3}
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder="Add Comment"
                          style={{ marginTop: 8 }}
                        />
                      </>
                    }
                  />
                }
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
              <Spin />
            </div>
          )}
        </>
      )}
      {contextHolder}
    </>
  );
};

export default ReviewUpload;
